import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { not } from 'ember-truth-helpers';
import type { LookerScheduledPlan } from 'lumen/models';

interface MarketCustomReportingTableCellsTdReportActionsSignature {
  Args: {
    cellData: {
      report: LookerScheduledPlan;
    };
  };
}

export default class MarketCustomReportingTableCellsTdReportActions extends Component<MarketCustomReportingTableCellsTdReportActionsSignature> {
  @service declare alerting: Services['alerting'];
  @service declare customReporting: Services['custom-reporting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  deleteReportTask = task({ drop: true }, async (): Promise<void> => {
    const { report } = this.args.cellData;

    const isConfirmed = await this.alerting.showWarningAlert({
      title: this.intl.t('custom_reporting.delete_scheduled_report.title', {
        title: report.name,
      }),
      text: this.intl.t('custom_reporting.delete_scheduled_report.description'),
      dangerMode: true,
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t('general.delete'),
        },
      },
    });

    if (!isConfirmed) {
      return;
    }

    try {
      await report.destroyRecord();
    } catch (e) {
      await this.alerting.showErrorAlert();
      this.errorTracking.captureException(e);
    }
  });

  @action editReport(): void {
    const { report } = this.args.cellData;

    if (!report.isDashboard) {
      return;
    }

    this.customReporting.pendingScheduleRequest = true;
    this.router.transitionTo(
      `${this.customReporting.baseRoute}.dashboard`,
      report.belongsTo('dashboard').id(),
    );
  }

  <template>
    <div class="report-actions flex flex-row gap-100">
      <PrismButton
        @buttonType="ghost"
        @icon="edit-pencil"
        @isDisabled={{not @cellData.report.isDashboard}}
        @onClick={{this.editReport}}
        @size="small"
        data-test-id={{concat "edit-report-" @cellData.report.id}}
      />
      <PrismButton
        @buttonType="ghost"
        @icon="bin"
        @onClick={{this.deleteReportTask.perform}}
        @size="small"
        data-test-id={{concat "delete-report-" @cellData.report.id}}
      />
    </div>
  </template>
}
